import { Show, createResource } from 'solid-js';
import { useParams } from '@solidjs/router';
import { useServer } from 'Shared/SiteProvider';
import { CreateStoreProvider } from 'Shared/StoreProvider';
import { Suspense } from 'solid-js';
import { Checkbox, SingleSelect } from 'Shared/forms/Inputs';
import { Wrap } from 'Shared/forms/Wrap';


export const pageName = 'backend/TestFields';

/*
	HOW TO MANAGE SUBDOCS

	You have 2 choices - placing the key field inside or outside the subdoc containing the details.

	1) Inside: you will need to supply a 'key' UNLESS its a boolean control (eg a checkbox). 
	2) Outside: you will need to supply a 'name'.

	In some cases you will want to keep the last subdoc when the key field is null...
		most likely when using a checkbox. I would suggest deleting out everything is the better default 
		as stale data looks messy, but for large subdocs you'll want to preserve the data to prevent whoopsies.

	To keep the old data you will need to place your key OUTSIDE, because the inside approach omits 
		the key field and uses the existence of the subdoc to provide the clause of the checkbox.

	If 'onChange' returns undefined the subdoc will be deleted.
*/


export default function TestFieldsPage()
{		
	const server = useServer();
	const params = useParams();

	/* 
		TO USE: create a 'testFields' table with an entry that includes a siteId.
	*/

	const [fields] = createResource(() => server.read.query({page:pageName,permission:'read',params:params}));

	return (
		<Suspense>
			<Show when={fields()}> 
				<CreateStoreProvider server={server} page={pageName} permission='edit' initial={fields()} docId={fields()._id} location={[]}>
					<Selects />
					<SelectWithInitialValue />
					<SelectWithDefaultValue />
					<Checkboxes />
					<Others />
				</CreateStoreProvider>
			</Show>
		</Suspense>
	);
}

function Selects()
{
	return (
		<section>
			<h3>Single Selects</h3>
			<p>
				Single selects seem to be the the main event wrt managing subdocs 
				(with arrays mostly being managed by repeaters). Note though that any field type CAN potentially 
				be used to manage a subdoc. It seems to make most sense to treat the single select as the
				basis for creating this part of the framework and to shoehorn in the other fields later
				as necessary.
			</p>

			<Wrap label='Select outside the subdir'
				notes='When null deletes out the type field too'
			>
				<SingleSelect field='selectOutside'
					options={{xxx:'XXX', yyy:'YYY'}}
					subdoc={{
						name: 'selectOutsideDetails',
						onChange: (newVal, _oldVal, oldSubdoc) => {
							if (newVal === 'xxx') return {extra1:true};
							if (newVal === 'yyy') return {extra2:true};
							return undefined;
						}
					}}
				/>
			</Wrap>

			<Wrap label='Select inside the subdir' >
				<SingleSelect field='selectInside'
					options={{xxx:'XXX', yyy:'YYY'}}
					subdoc={{
						key: 'type',
						onChange: (newVal, _oldVal, oldSubdoc) => {
							if (newVal === 'xxx') return {extra1:true};
							if (newVal === 'yyy') return {extra2:true};
							return undefined;
						}
					}}
				/>
			</Wrap>
		</section>
	);
}

function SelectWithDefaultValue()
{
//TODO maybe test with subdoc
//NOTE currently changing to 'xxx' isn't deleting the field, but sould and maybe should
	return (
		<section>
			<h3>Selects with default value</h3>
			<p>
				Treat null as being equivalent to the specified value.
				defaultValue is incomptable with initialValue and required. 
			</p>

			<Wrap label='Select outside with initial value'>
				<SingleSelect field='selectDefaultValue'
					options={{xxx:'XXX', yyy:'YYY'}}
					defaultValue='xxx'
				/>
			</Wrap>
		</section>
	);
}

function SelectWithInitialValue()
{
//TODO add a test case for defaultValue 
//TODO cf checkboxes	

	return (
		<section>
			<h3>Auto create in Selects using initial value and required</h3>

			<Wrap label='Select outside with initial value'>
				<SingleSelect field='selectOutsideInitialValue'
					required={true}
					options={{xxx:'XXX', yyy:'YYY'}}
					initialValue='xxx'
					subdoc={{
						name: 'selectOutsideInitialValueDetails',
						onChange: (newVal, _oldVal, oldSubdoc) => {
							if (newVal === 'xxx') return {extra1:true};
							if (newVal === 'yyy') return {extra2:true};
							return undefined;
						}
					}}
				/>
			</Wrap>

			<Wrap label='Select inside with initial value'>
				<SingleSelect field='selectInsideInitialValue'
					required={true}
					options={{xxx:'XXX', yyy:'YYY'}}
					initialValue='xxx'
					subdoc={{
						key: 'type',
						onChange: (newVal, _oldVal, oldSubdoc) => {
							if (newVal === 'xxx') return {extra1:true};
							if (newVal === 'yyy') return {extra2:true};
							return undefined;
						}
					}}
				/>
			</Wrap>
		</section>
	);
}

function Checkboxes()
{
	return (
		<section>
			<h3>Checkboxes</h3>
			<p>
				Select boxes have select fields - so think of checkboxes in the same way with 
				true and false being the two possible values.
			</p>
			<p>
				Now we typically can imply the true/false value from the existence or not of the data
				or select field, so we add some hacky code to do this. The gain is that the DB is 
				nice and clean and free of strange framework-related artifacts.
			</p>

			<Wrap label='Checkbox outside the subdir (none|value)'>
				<Checkbox field='checkboxOutside'
					subdoc={{
						name: 'checkboxOutsideDetails',
						onChange: (newVal, _oldVal, oldSubdoc) => {
							return newVal ? {extra1:true} : undefined;
						}
					}}
				/>
			</Wrap>

			<Wrap label='Checkbox outside the subdir (value 1 | value 2)'>
				<Checkbox field='checkboxOutsideTwoValues'
					subdoc={{
						name: 'checkboxOutsideTwoValuesDetails',
						onChange: (newVal, _oldVal, oldSubdoc) => {
							return newVal ? {extra1:true} : {extra2:true};
						}
					}}
				/>
			</Wrap>

			<Wrap label='Checkbox inside the subdir' 
				notes='No key needed - we use existence of subdoc as the "key"'
			>
				<Checkbox field='checkboxInside1'
					subdoc={{
						onChange: (newVal, _oldVal, oldSubdoc) => {
							return newVal ? {extra1:true} : undefined;
						}
					}}
				/>
			</Wrap>
		</section>
	);
}

function Others()
{
	return (
		<section>
			<h3>Others</h3>
			<p>
				Multiselects are currently being used by styles and could be implemented.
			</p>
			<p>
				Any type of field could potentially be extended to manage related subdocs.
			</p>
		</section>
	);
}

